import { SubscriptionDTO } from '@/types';

/**
 * Confirmation copy for adding one more user or company when the plan's included count is
 * already used up — those are allowed but billed monthly, so the cost is stated before it is
 * incurred rather than surfacing on an invoice.
 *
 * Null means there is nothing to warn about: it still fits within the plan, the plan caps
 * instead of billing (the server refuses those with a 409 and its own upgrade message), or no
 * rate is configured yet — the plan prices are placeholders until billing lands, and
 * "adds $0.00 per month" would read as a bug.
 */
export function extraCostPrompt(
  subscription: SubscriptionDTO | undefined,
  noun: 'user' | 'company',
  used: number,
): string | null {
  if (!subscription) return null;

  const isUser = noun === 'user';
  const included = isUser ? subscription.max_seats : subscription.max_companies;
  const canBuy = isUser ? subscription.can_buy_seats : subscription.can_buy_companies;
  const priceCents = isUser
    ? subscription.extra_seat_price_cents
    : subscription.extra_company_price_cents;

  if (!canBuy || !priceCents || priceCents <= 0 || used < included) return null;

  const price = (priceCents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return `Your ${subscription.plan_name} plan includes ${included} ${isUser ? 'users' : 'companies'} ` +
    `and you already have ${used}. Each additional ${noun} adds ${price} per month to your bill, ` +
    `${isUser ? 'for as long as their account stays active' : 'for as long as it exists'}. Continue?`;
}

/**
 * Whether one more company can be created right now: metered plans always can, capping plans
 * only while still under the included count.
 *
 * An undefined subscription (still loading, or the fetch failed) reads as "no" so a plan-capped
 * org never flashes a picker or an Add button it can't use.
 */
export function canAddCompany(subscription: SubscriptionDTO | undefined, used: number): boolean {
  return !!subscription && (subscription.can_buy_companies || used < subscription.max_companies);
}
